import { SlotGame } from '@nolimitcity/slot-game/bin/SlotGame';
import { SpinePlayer } from '../../../../core/displayobject/SpinePlayer';
import { PresentationState } from '../../../../core/gameflow/controller/Presentation';
import { ReelState } from '../../../../core/reel/ReelEngineView';
import { Helper } from '../../../../core/utils/Helper';
import { MathHelper } from '../../../../core/utils/MathHelper';
import { StateHandler } from '../../../../core/utils/StateHandler';
import { Beheaded } from '../../../Beheaded';
import { SoundConfig } from '../../../config/sound/SoundConfig';
import { SoundManager } from '../../../config/sound/SoundManager';
import { BeheadedEngineController } from '../../../reels/BeheadedEngineController';
import { BeheadedEngineManager } from '../../../reels/BeheadedEngineManager';
import { GameData } from '../../../server/data/GameData';
import { InitData } from '../../../server/data/InitData';
import { StartPresentation } from './StartPresentation';
import gsap from 'gsap';
import { SplashController, SplashIndex } from '../../../modules/splashScreen/SplashController';
import { GameSetting } from '../../../../core/setting/GameSetting';

export class StartStates extends StateHandler {
    public static readonly AVALANCHING: string = 'avalanching';
    public static readonly SCATTER_COLLECT: string = 'scatter_collect';
    public static readonly UPGRADING: string = 'upgrading';
    public static readonly COLLECTING: string = 'collecting';
    public static readonly SFS_INTRO: string = 'intro';
    public static readonly SFS_RESTORE: string = 'intro_restore';
    public static readonly DONE: string = 'done';
}

export class AvalancheStartPresentation extends StartPresentation {
    protected nextModeData: string;
    protected reelsAfterCollapseData: string[][];
    protected evaluatedAreaData: string[][];
    private currentLevel: number;
    private startingLevel: number;
    private nextLevel: number;

    private isRestore: boolean;
    private nextWinMultiplier: number;

    private collectedSymbols: number;
    private collectedSouls: number;
    private startFS: number;
    private endFS: number;

    private cutIndx: number[];

    protected startState: StartStates = new StartStates(StartStates.AVALANCHING);

    protected isWin: boolean;

    public static playScatterSound = false;

    public checkForActivation(data: GameData): boolean {
        return !!data.Win.singleWin && !data.Win.brokeTheBank;
    }

    public setInitData(data: InitData): void {
        this.setGamedData(data);
    }

    public setGamedData(data: GameData): void {
        this.isWin = !!data.Win.singleWin;
        this.nextModeData = data.nextMode;
        this.reelsAfterCollapseData = data.reelsAfterCollapse;
        this.evaluatedAreaData = data.evaluatedArea;
        this.startingLevel = this.currentLevel = data.currentLevel;
        this.nextLevel = data.nextLevel;
        this.nextWinMultiplier = data.nextWinMultiplier;

        this.isRestore = data.isRestoreState;

        this.endFS = data.freespinsLeft;
        this.startFS = data.freespinsLeft - (data.raw.addedNumberOfFreespinsThisSpin - data.Reel.ammScatterCaught);

        this.collectedSymbols = data.collectedSymbols;
        this.collectedSouls = data.nextCollectedSouls;
        this.cutIndx = data.raw.cutIndex;

        BeheadedEngineManager.RAll(reel => {
            reel.applyEvalReel(data.Reel.evaldata[reel.reelID]);
        })
    }

    protected addExtraForStart(reel: BeheadedEngineController) {
        reel.markSymbolCuts();

        reel.AllSticky.forEach((sym, indx) => {
            sym.applyDropFx(reel.startDuration);

            if (!sym.isLowValue && !sym.isScatter && this.currentLevel != 5) {
                gsap.to(this, {
                    duration: 0.01,
                    onComplete: () => {
                        Beheaded.collectorBar.addHeads(sym.symName);
                    },
                    delay: reel.startDuration / 4 + (indx * reel.startDuration) / 4,
                });
            }
        });
    }

    preStart(): void {
        if (this.isRestore && this.currentLevel === 5) {
            this.startState.set(StartStates.SFS_RESTORE);
        } else this.startState.set(StartStates.AVALANCHING);

        BeheadedEngineManager.RAll((rc: BeheadedEngineController) =>
            rc.createAvalancheStart({
                start: () => (this.addExtraForStart ? this.addExtraForStart(rc) : undefined),
                started: () => {
                    if (!rc.reelID) {
                        Beheaded.collectorBar.setValue(this.collectedSymbols, this.nextLevel);
                    }
                },
            })
        );
    }

    startReels(): void {
        let delay: number = SpinePlayer.fetchDuration('rope_cut', 'rope_cut') * 0.7;
        // rope_cut_withsmoke
        const startReel: number = BeheadedEngineManager.findReelWithState(ReelState.START, true);
        BeheadedEngineManager.RAll((reel) => {
            if (reel.reelID == startReel)
                this.timeline.add(() => {
                    // SoundManager.playSoundEffect(SoundConfig.REELS_SPINNING, Number.MAX_SAFE_INTEGER, 1);
                }, delay);

            this.timeline.add(() => reel.startReel(), delay);

            if (reel.isActive) delay += reel.startDelay;
        });
        // if only one reel starts, there is no time delay. Need to add some duration to trigger the elements in an gsap timeline.
        this.timeline.duration() == 0 ? this.timeline.to(this, { duration: MathHelper.frameToSeconds(1) }) : null;
        this.timeline.to(this, 0.3, {});
    }

    // Whenever we restart the presentation, so does the timeline. Easier to divide them in section like this.
    createPresentation(skip?: Function) {

        switch (this.startState.value) {
            case StartStates.SFS_RESTORE:
                this.onSfsIntro();
                this.startState.set(StartStates.AVALANCHING);
                break;
            case StartStates.AVALANCHING:
                if (skip) skip(false);
                Beheaded.souldWildController.soulWildRemove();
                Beheaded.ropeCutter.cutIndivi(this.cutIndx);
                super.createPresentation(skip);
                this.onCuttingStuff();
                this.startState.set(StartStates.UPGRADING);
                break;
            case StartStates.UPGRADING:
                if (skip) skip(true);
                this.onUpgradingStuff();
                break;
            case StartStates.SFS_INTRO:
                if (skip) skip(false);
                this.onSfsIntro();
                this.startState.set(StartStates.COLLECTING);
                break;
            case StartStates.COLLECTING:
                if (skip) skip(true);
                this.startState.set(StartStates.DONE);
                this.onCollecting();
                break;
            default:
                this.PresentationState.setInactive();
        }

        super.skip();
    }

    onCuttingStuff(): void {
        this.PresentationState.set(PresentationState.ACTIVATED);

        const delay = this.timeline.duration() / 2;

        // const soulDurr = Beheaded.souldWildSouls.leaveSouls(this.evaluatedAreaData, this.cutIndx).duration();

        this.timeline.add(Beheaded.souldWildSouls.leaveSouls(this.evaluatedAreaData, this.cutIndx),0)

        let ammScattersCollected: number = 0;
        AvalancheStartPresentation.playScatterSound = false;

        if (this.nextWinMultiplier != Beheaded.baseMultiplier.currentMult) {
            BeheadedEngineManager.RAll((reel) => {
                ammScattersCollected += reel.ammScattersCollected;
                this.timeline.add(() => {
                    reel.reelID === 4 && (AvalancheStartPresentation.playScatterSound = true);
                    reel.fetchDroppedScatters();
                }, delay);
            });

            this.timeline.to(this, { duration: GameSetting.isFastSpin ? 0.5 : 1 });

            this.timeline.add(() => {
                Beheaded.overlayBG.flagHit(ammScattersCollected);
            }, 0.5);
            this.timeline.add([ Beheaded.counter.displayMult(this.nextWinMultiplier, 1)],  '-=0.6');
            this.timeline.to(this, { duration: 0.2 }, '<');
        }

        if (this.nextLevel === 5) {
            this.timeline.add(() => Beheaded.overlayBG.flagHit(3), 0.5);
        }
    }

    onUpgradingStuff(): void {
        if (this.startingLevel < this.nextLevel) {
            this.startingLevel++;

            this.startFS += this.startingLevel == 5 ? 6 : 2;
            let startPos: number = 0;

            BeheadedEngineManager.RAll((reel) => {
                this.timeline.to(reel, { duration: 0.5, alpha: 0.15 }, startPos);
            });

            this.timeline.add(() => {
                Beheaded.souldWildSouls.soulsToAlpha(0.15);
            }, startPos);

            this.timeline.add(Beheaded.backgroundCharacter.showSmokeLevelUp(this.startingLevel), startPos);
            this.timeline.add(Beheaded.backgroundCharacter.showCharacterLevelUp(this.startingLevel), startPos);

            this.timeline.add(
                Beheaded.collectorBar.moveLvlUpSymToBar(this.startingLevel, this.currentLevel == 4 && this.nextLevel == 5),
                startPos
            );

            startPos += this.timeline.duration();

            this.timeline.add(() => {
                SlotGame.keypadPlugin.setZeroBetSpinCounter(this.startFS);
            }, startPos - 0.65);

            this.timeline.add(() => {
                Beheaded.collectorBar.updateTheBar();
                Beheaded.souldWildController.onLevelUpIndicator();

                Beheaded.overlayBG.upgradeBGOverlay(this.startingLevel);
                Beheaded.background.upgradeBG(this.startingLevel);
            }, startPos);

            if (this.startingLevel) {
                BeheadedEngineManager.RSAll((r, sym) => {
                    if ([5, 4, 3, 2, 1][this.startingLevel - 1] <= parseInt(sym.symName[1]))
                        this.timeline.add(() => {
                            sym.chargeSymbol();
                        }, startPos - 0.25);
                });
            }

            this.timeline.add(() => Beheaded.souldWildSouls.soulsToAlpha(1, 1.25), startPos - 0.25);

            BeheadedEngineManager.RAll((reel) => {
                this.timeline.to(reel, { duration: 1.25, alpha: 1 }, startPos - 0.25);
            });
        } else {
            this.startState.set(
                this.currentLevel != this.nextLevel && this.nextLevel == 5 ? StartStates.SFS_INTRO : StartStates.COLLECTING
            );
        }
    }

    onSfsIntro(): void {
        SoundManager.fadeOutAmbient(100, 0);

        SlotGame.keypadPlugin.hide(1);
        SlotGame.keypadPlugin.setZeroBetSpinCounter(-1);
        this.timeline.add(
            SplashController.instance.play(SplashIndex.INTRO, true, true, () => {
                Beheaded.keypadPlugin.show();
                SlotGame.keypadPlugin.setZeroBetSpinCounter(this.endFS);
            })
        );
    }

    skip(): void {
        super.skip();
        this.timeline && this.timeline.isActive() && this.timeline.totalProgress(1);
    }

    onCollecting(): void {
        if (this.nextLevel != 5) Beheaded.overlayBG.flagHideHit();
        if (Beheaded.souldWildSouls.SoulVictims.length)
            this.timeline.add(Beheaded.souldWildSouls.suckSouls(Beheaded.souldWildController.SWIndicators));
    }

    done(): void {
        if(Beheaded.souldWildSouls.SoulVictims.length)
            Beheaded.souldWildSouls.reset();
    }
}
